import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReactCodeInput from 'react-verification-code-input';
import { selectors } from '../../LoyaltyPage/reducer';
import * as actions from '../../LoyaltyPage/actions';
import { selectors as loginSelectors } from '../../LoginPage/reducer';
import LoyaltyApplyScreen from '../../LoyaltyPageSeparated/components/LoyaltyApplyScreen';
import * as V3 from '../../LoyaltyPageSeparated/LoyaltyPageSeparated.styles';
import * as Styled from './Coupon.styles';
import {
  CheckedCoupon,
  confirmationPayload,
  couponAmounts,
  couponReceiptError,
  couponOrderPhone,
  isCouponCode,
  money,
  normaliseCouponCode,
  quoteError,
  receiptFingerprint,
  standartItems,
} from './model';
import { CouponError, CouponService, couponErrorKey } from './service';
import {
  clearCouponRecovery,
  CouponRecovery,
  saveCouponRecovery,
} from './recovery';
import { couponOrderNumber } from './order';
import { watchCouponReceipt } from './monitor';
import type {
  BonusHelpState,
  CouponHelpState,
} from '../components/loyaltyHelpContent';

const DEV_COUPON_ORDER_PHONE = '70000000000';

interface Props {
  entryCode: string | null;
  partnerPercent: number;
  isStadaBonusesCredit: boolean;
  isPartnersPayment: boolean;
  setShowWithBonuses: (value: boolean) => void;
  onOrderConfirmed: (
    items?: { ware_id: string; price: number; quantity: number }[],
    couponCheckout?: boolean
  ) => void;
  onBack: () => void;
  onLockedChange: (locked: boolean) => void;
  onCouponConfirmed: (recovery: CouponRecovery) => void;
  onRestart: (completion?: { docId: string; orderNumber?: string }) => void;
  api?: typeof CouponService;
  onHelpStateChange?: (state: CouponHelpState | null) => void;
  onBonusHelpStateChange?: (state: BonusHelpState | null) => void;
}
const formatAmount = (amount: number) =>
  amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 });

const LoyaltyCouponBonusScreen: React.FC<Props> = ({
  entryCode,
  partnerPercent,
  isStadaBonusesCredit,
  isPartnersPayment,
  setShowWithBonuses,
  onOrderConfirmed,
  onBack,
  onLockedChange,
  onCouponConfirmed,
  onRestart,
  api = CouponService,
  onHelpStateChange,
  onBonusHelpStateChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const receipt = useSelector(selectors.chosenReceipt);
  const client = useSelector(selectors.clientInfo);
  const stada = useSelector(selectors.stadaInfo);
  const user = useSelector(loginSelectors.userData);
  const couponOnlyPhone = couponOrderPhone(
    process.env.REACT_APP_DEV_AREA === 'true',
    process.env.REACT_APP_COUPON_ORDER_PHONE || DEV_COUPON_ORDER_PHONE
  );
  const [code, setCode] = useState(entryCode ?? '');
  const [checked, setChecked] = useState<CheckedCoupon | null>(null);
  const [confirmedAmounts, setConfirmedAmounts] = useState<ReturnType<
    typeof couponAmounts
  > | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [useBonuses, setUseBonuses] = useState(false);
  const [bonusCode, setBonusCode] = useState('');
  const bonusInput = useRef<ReactCodeInput | null>(null);
  const [uncertain, setUncertain] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [monitorError, setMonitorError] = useState('');
  const monitor = useRef<ReturnType<typeof watchCouponReceipt> | null>(null);
  const latestOrderNumber = useRef('');
  const requestId = useRef(0);
  const operationRef = useRef(false);
  const checkController = useRef<AbortController | null>(null);
  const latestReceipt = useRef(receipt);
  latestReceipt.current = receipt;
  const locked = busy || uncertain || confirmed;
  const balance = client?.balance ?? 0;
  const stadaBalance = isStadaBonusesCredit ? stada?.balance ?? 0 : 0;
  const amounts =
    confirmedAmounts ??
    (checked
      ? couponAmounts(
          checked,
          balance,
          useBonuses,
          partnerPercent,
          stadaBalance
        )
      : null);
  const offeredBonus = checked
    ? couponAmounts(checked, balance, true, partnerPercent, stadaBalance).bonus
    : 0;
  useEffect(() => {
    onHelpStateChange?.({
      active: !!entryCode || code.length > 0 || !!checked,
      checked: !!checked,
      busy,
      useBonuses,
      error: error || monitorError,
      uncertain,
      missingOrder: confirmed && !orderNumber,
      bonusAvailable: offeredBonus > 0,
    });
  }, [
    onHelpStateChange,
    entryCode,
    code.length,
    checked,
    busy,
    useBonuses,
    error,
    monitorError,
    uncertain,
    confirmed,
    orderNumber,
    offeredBonus,
  ]);
  useEffect(() => () => onHelpStateChange?.(null), [onHelpStateChange]);

  useEffect(() => {
    // This existing PL input stores its cells internally; reset them too.
    // eslint-disable-next-line no-underscore-dangle
    if (!bonusCode) bonusInput.current?.__clearvalues__();
  }, [bonusCode]);

  useEffect(() => {
    onLockedChange(locked);
  }, [locked, onLockedChange]);
  useEffect(() => {
    if (!uncertain && !confirmed) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [uncertain, confirmed]);
  useEffect(
    () => () => {
      requestId.current += 1;
      checkController.current?.abort();
    },
    []
  );
  useEffect(() => {
    if (!checked || locked) return;
    if (!receipt || receiptFingerprint(receipt) !== checked.fingerprint) {
      setChecked(null);
      setBonusCode('');
      setUseBonuses(false);
      setError('RECEIPT_CHANGED');
    }
  }, [receipt, checked, locked]);

  // A gateway omission is not cashier completion. Keep the exact receipt and
  // its reservation locked until consecutive valid local reads say it ended.
  useEffect(() => {
    if (!confirmed || !checked) return undefined;
    const docId = String(checked.receipt.doc_id);
    const watcher = watchCouponReceipt(api, docId, {
      onProgress: (progress) => {
        setMonitorError(progress.syncError ? 'RECEIPT_MONITOR_ERROR' : '');
        if (!progress.orderNumber) return;
        latestOrderNumber.current = progress.orderNumber;
        setOrderNumber(progress.orderNumber);
        try {
          saveCouponRecovery({
            sourceCode: user?.sourceCode ?? '',
            docId,
            phase: 'confirmed',
            orderNumber: progress.orderNumber,
          });
        } catch {
          setMonitorError('RECOVERY_SAVE_ERROR');
        }
      },
      onEnded: () =>
        onRestart({ docId, orderNumber: latestOrderNumber.current }),
      onError: () => setMonitorError('RECEIPT_MONITOR_ERROR'),
    });
    monitor.current = watcher;
    return () => {
      watcher.stop();
      monitor.current = null;
    };
  }, [api, confirmed, checked, onRestart, user?.sourceCode]);

  const checkCoupon = async () => {
    if (operationRef.current || locked) return;
    const submittedCode = normaliseCouponCode(code);
    if (!isCouponCode(submittedCode)) {
      setError('INVALID_CODE');
      return;
    }
    const currentReceipt = latestReceipt.current;
    const localError = couponReceiptError(currentReceipt);
    if (localError || !currentReceipt) {
      setError(localError ?? 'NO_RECEIPT');
      return;
    }
    const fingerprint = receiptFingerprint(currentReceipt);
    requestId.current += 1;
    const currentRequest = requestId.current;
    checkController.current?.abort();
    const controller = new AbortController();
    checkController.current = controller;
    setBusy(true);
    setError('');
    try {
      const quote = await api.check(
        submittedCode,
        currentReceipt.doc_id,
        controller.signal
      );
      if (currentRequest !== requestId.current) return;
      if (
        !latestReceipt.current ||
        receiptFingerprint(latestReceipt.current) !== fingerprint
      ) {
        throw new CouponError('RECEIPT_CHANGED');
      }
      const problem = quoteError(currentReceipt, quote);
      if (problem) throw new CouponError(problem);
      setCode(submittedCode);
      setChecked({
        code: submittedCode,
        quote,
        receipt: currentReceipt,
        fingerprint,
      });
      setBonusCode('');
      setUseBonuses(false);
    } catch (error) {
      if (currentRequest === requestId.current && !controller.signal.aborted) {
        setChecked(null);
        setError(couponErrorKey(error));
      }
    } finally {
      if (currentRequest === requestId.current) setBusy(false);
    }
  };
  useEffect(() => {
    if (entryCode && receipt) checkCoupon();
    // Re-check only on entry or receipt selection, never on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryCode, receipt?.doc_id]);

  const confirm = async () => {
    if (operationRef.current || !checked || !amounts || uncertain || confirmed)
      return;
    if (
      !user?.sourceCode ||
      (useBonuses && (!client?.phone || !/^\d{6}$/.test(bonusCode)))
    )
      return;
    if (!client?.phone && !couponOnlyPhone) {
      setError('COUPON_ORDER_PHONE_MISSING');
      return;
    }
    operationRef.current = true;
    setBusy(true);
    setError('');
    const expected = amounts;
    let confirmationStarted = false;
    let serverConfirmed = false;
    try {
      const fresh = await api.receipt(checked.receipt.doc_id);
      if (receiptFingerprint(fresh) !== checked.fingerprint)
        throw new CouponError('RECEIPT_CHANGED');
      const quote = await api.check(checked.code, fresh.doc_id);
      const problem = quoteError(fresh, quote);
      if (problem) throw new CouponError(problem);
      const active = {
        code: checked.code,
        quote,
        receipt: fresh,
        fingerprint: receiptFingerprint(fresh),
      };
      const recalculated = couponAmounts(
        active,
        balance,
        useBonuses,
        partnerPercent,
        stadaBalance
      );
      setChecked(active);
      if (
        money(quote.price_with_discount) !==
          money(checked.quote.price_with_discount) ||
        recalculated.payable !== expected.payable ||
        recalculated.bonus !== expected.bonus
      ) {
        setBonusCode('');
        throw new CouponError('RECALCULATED');
      }
      const payload = confirmationPayload(
        active,
        user.sourceCode,
        recalculated.items,
        client?.phone,
        useBonuses ? bonusCode : undefined,
        client?.phone ? undefined : couponOnlyPhone
      );
      // Protect against reload before the first external mutation. No OTP,
      // phone or coupon code is persisted in this marker.
      try {
        saveCouponRecovery({
          sourceCode: user.sourceCode,
          docId: active.receipt.doc_id,
          phase: 'confirm',
        });
      } catch {
        throw new CouponError('RECOVERY_UNAVAILABLE');
      }
      confirmationStarted = true;
      // The existing PL flow creates the PA order through /confirm/partner
      // before the cashier payment method is finalized. Coupons must follow
      // that same single order-creating request instead of reserving through
      // ordinary /confirm and waiting for the receipt to close.
      const result = await api.confirm({ ...payload, isPartner: true });
      serverConfirmed = true;
      const confirmedOrderNumber = couponOrderNumber(result);
      latestOrderNumber.current = confirmedOrderNumber;
      setOrderNumber(confirmedOrderNumber);
      // The coupon response may confirm only its reservation. Keep that state
      // separately; it must not announce a PL order that the API did not return.
      if (confirmedOrderNumber)
        dispatch(
          actions.submitLoyaltyOrder.success({
            ...result,
            docId: active.receipt.doc_id,
            bonusUsed: useBonuses,
            result: {
              ...(typeof result.result === 'object' ? result.result : {}),
              id: confirmedOrderNumber,
            },
          })
        );
      const items = standartItems(recalculated.items);
      setConfirmedAmounts(recalculated);
      setConfirmed(true);
      const recovery: CouponRecovery = {
        sourceCode: user.sourceCode,
        docId: active.receipt.doc_id,
        phase: 'confirmed',
        ...(confirmedOrderNumber ? { orderNumber: confirmedOrderNumber } : {}),
      };
      try {
        saveCouponRecovery(recovery);
      } catch {
        throw new CouponError('RECOVERY_SAVE_ERROR');
      }
      onCouponConfirmed(recovery);
      // Payment starts only after a real PA exists. The parent keeps payment
      // choices for PL clients and immediately sends coupon-only orders to
      // the cashier; both flows use these confirmed final prices for /scan.
      if (confirmedOrderNumber) onOrderConfirmed(items, true);
    } catch (error) {
      if (confirmationStarted && !serverConfirmed) {
        if (!(error instanceof CouponError) || error.uncertain) {
          setUncertain(true);
          setError('CONFIRM_UNKNOWN');
          return;
        }
        // A definitive rejection never changed the cashier price. The next
        // explicit attempt must re-read the receipt and re-check the coupon.
        try {
          clearCouponRecovery(user.sourceCode);
        } catch {
          setUncertain(true);
          setError('RECOVERY_CLEAR_ERROR');
          setBonusCode('');
          return;
        }
        if (error.key === 'BONUS_CODE_INVALID') setBonusCode('');
      }
      if (couponErrorKey(error) === 'RECEIPT_CHANGED') setBonusCode('');
      setError(couponErrorKey(error));
    } finally {
      operationRef.current = false;
      setBusy(false);
    }
  };
  const editor = (
    <Styled.Editor aria-label={t('LoyaltyCoupons.TITLE')}>
      <label htmlFor="coupon-code">{t('LoyaltyCoupons.TITLE')}</label>
      <div className="form-row">
        <input
          id="coupon-code"
          value={code}
          maxLength={6}
          autoComplete="off"
          spellCheck={false}
          disabled={locked}
          aria-describedby="coupon-hint"
          onChange={(event) => {
            if (!/^[A-Za-z0-9_]*$/.test(event.target.value)) return;
            requestId.current += 1;
            setCode(event.target.value);
            setChecked(null);
            setError('');
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              checkCoupon();
            }
          }}
        />
        <V3.V3SecondaryButton
          type="button"
          style={{ width: 'auto' }}
          disabled={locked}
          onClick={checkCoupon}
        >
          {t(busy ? 'LoyaltyCoupons.CHECKING' : 'LoyaltyCoupons.CHECK')}
        </V3.V3SecondaryButton>
      </div>
      <p id="coupon-hint">{t('LoyaltyCoupons.CODE_HINT')}</p>
      {checked && <p className="result">{t('LoyaltyCoupons.AVAILABLE')}</p>}
      {error && (
        <Styled.Error role="alert">
          {t(`LoyaltyCoupons.Errors.${error}`)}
        </Styled.Error>
      )}
    </Styled.Editor>
  );
  if (!checked && client && !entryCode) {
    return (
      <LoyaltyApplyScreen
        setShowWithBonuses={setShowWithBonuses}
        partnerPercent={partnerPercent}
        isStadaBonusesCredit={isStadaBonusesCredit}
        onOrderConfirmed={onOrderConfirmed}
        isPartnersPayment={isPartnersPayment}
        variant="v3"
        onBackToReceipt={onBack}
        couponContent={editor}
        externalBlocked={busy || code.length > 0}
        onHelpStateChange={onBonusHelpStateChange}
      />
    );
  }
  let confirmLabel = client
    ? 'LoyaltyV3.NO_WITHOUT_BONUSES'
    : 'LoyaltyCoupons.CONFIRM';
  if (useBonuses) confirmLabel = 'LoyaltyV3.CONFIRM_AND_PAY';
  if (busy) confirmLabel = 'LoyaltyCoupons.CONFIRMING';
  let checkoutTitle = 'LoyaltyCoupons.CHECKOUT_TITLE';
  let checkoutHint = 'LoyaltyCoupons.CASHIER_ONLY';
  if (confirmed) {
    checkoutTitle = 'LoyaltyCoupons.ORDER_PENDING_TITLE';
    checkoutHint = 'LoyaltyCoupons.ORDER_NOT_CONFIRMED';
    if (orderNumber) {
      checkoutHint = 'LoyaltyCoupons.CASH_NOT_VERIFIED';
    }
  }
  const ConfirmButton =
    client && !useBonuses ? V3.V3SecondaryButton : V3.V3PrimaryButton;
  const bonusControls = client && !uncertain && (
    <>
      <V3.V3BonusLimitHint>
        {t('LoyaltyCoupons.BONUS_AFTER_COUPON')}
      </V3.V3BonusLimitHint>
      {useBonuses && (
        <V3.V3CodeField role="group" aria-labelledby="coupon-bonus-code-label">
          <label id="coupon-bonus-code-label">
            {t('LoyaltyV3.BONUS_CONFIRMATION_CODE')}
          </label>
          <ReactCodeInput
            ref={bonusInput}
            fields={6}
            fieldWidth={44}
            fieldHeight={48}
            placeholder={['0', '0', '0', '0', '0', '0']}
            values={[...bonusCode]}
            disabled={busy}
            onChange={(value) => {
              if (/^\d{0,6}$/.test(value)) setBonusCode(value);
            }}
          />
          <small>{t('LoyaltyV3.BONUS_CODE_HINT')}</small>
        </V3.V3CodeField>
      )}
    </>
  );
  return (
    <V3.V3BonusScreen>
      <V3.V3BonusContent>
        {!locked && (
          <V3.V3BackButton type="button" onClick={onBack}>
            ← {t('LoyaltyV3.BACK_TO_RECEIPT')}
          </V3.V3BackButton>
        )}
        <V3.V3BonusTitle>{t(checkoutTitle)}</V3.V3BonusTitle>
        {!confirmed && editor}
        {checked && amounts && (
          <>
            <V3.V3BonusSubtitle>{t(checkoutHint)}</V3.V3BonusSubtitle>
            <V3.V3ReceiptCaption>
              {t('LoyaltyV3.RECEIPT')} №{checked.receipt.doc_id}
              {orderNumber && (
                <>
                  {' '}
                  · {t('LoyaltyV3.ORDER_NUMBER')} {orderNumber}
                </>
              )}
            </V3.V3ReceiptCaption>
            <p>{checked.receipt.items[0].name} · 1</p>
            <V3.V3BonusTotals>
              <div>
                <span>{t('LoyaltyV3.RECEIPT_COST')}</span>
                <strong>{formatAmount(amounts.original)} ₸</strong>
              </div>
              <div className="discount">
                <span>{t('LoyaltyCoupons.DISCOUNT')}</span>
                <strong>− {formatAmount(amounts.coupon)} ₸</strong>
              </div>
              <div className="discount">
                <span>{t('LoyaltyV3.PHARMACY_BONUS_DISCOUNT')}</span>
                <strong>− {formatAmount(amounts.bonus)} ₸</strong>
              </div>
              <div className="payable">
                <span>{t('LoyaltyV3.CASHIER_PAYABLE')}</span>
                <strong>{formatAmount(amounts.payable)} ₸</strong>
              </div>
            </V3.V3BonusTotals>
            {confirmed ? (
              <>
                {error && (
                  <Styled.Error role="alert">
                    {t(`LoyaltyCoupons.Errors.${error}`)}
                  </Styled.Error>
                )}
                <Styled.Notice role="status">
                  {t('LoyaltyCoupons.ORDER_NOT_CONFIRMED')}
                </Styled.Notice>
                <V3.V3BonusSubtitle>
                  {t('LoyaltyCoupons.REDEMPTION_HINT')}
                </V3.V3BonusSubtitle>
                {monitorError && (
                  <Styled.Error role="alert">
                    {t(`LoyaltyCoupons.Errors.${monitorError}`)}
                  </Styled.Error>
                )}
              </>
            ) : (
              bonusControls
            )}
          </>
        )}
      </V3.V3BonusContent>
      {checked && (
        <V3.V3BonusFooter>
          {confirmed ? (
            <V3.V3SecondaryButton onClick={() => monitor.current?.check()}>
              {t('LoyaltyCoupons.CHECK_COMPLETION')}
            </V3.V3SecondaryButton>
          ) : (
            <>
              {client && !useBonuses && (
                <V3.V3PrimaryButton
                  disabled={busy || uncertain || offeredBonus <= 0}
                  onClick={() => setUseBonuses(true)}
                >
                  {t('LoyaltyV3.YES_USE_BONUSES')}
                </V3.V3PrimaryButton>
              )}
              <ConfirmButton
                disabled={
                  busy || uncertain || (useBonuses && bonusCode.length !== 6)
                }
                onClick={confirm}
              >
                {t(confirmLabel)}
              </ConfirmButton>
              {useBonuses && !busy && !uncertain && (
                <V3.V3SecondaryButton
                  onClick={() => {
                    setUseBonuses(false);
                    setBonusCode('');
                  }}
                >
                  {t('LoyaltyV3.NO_WITHOUT_BONUSES')}
                </V3.V3SecondaryButton>
              )}
            </>
          )}
        </V3.V3BonusFooter>
      )}
    </V3.V3BonusScreen>
  );
};
export default LoyaltyCouponBonusScreen;
